import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@shared/api';
import { ModelChip, ModelLogo, providerName } from '@shared/brand';
import { StatTile, TimeSeriesChart, seriesColor } from '@shared/charts';
import { compact, dateShort, dateTime, idr, ms, num, percent, relative } from '@shared/format';
import { CreateKeyModal, KeyRevealModal, type CreatedKey, type ProjectOption } from '../components/ApiKeyBlocks';
import { Alert, Badge, Card, Delta, Empty, Icon, Loading, PageHeader, Segmented } from '../components/ui';
import { MODALITY_LABEL, modalityOf, playgroundHref, popularModels, providerOf, type CatalogModel } from '../catalog';
import '../styles/dashboard.css';

interface Balance {
  available_balance: number;
  reserved_balance: number;
  total_balance: number;
}

interface DayRow {
  date: string;
  requests: number;
  tokens: number;
  cost_idr: number;
}

interface Summary {
  totals: {
    requests: number;
    successful_requests: number;
    failed_requests: number;
    input_tokens: number;
    cached_tokens: number;
    output_tokens: number;
    reasoning_tokens: number;
    cost_idr: number;
    avg_latency_ms: number;
  };
  by_day: DayRow[];
  by_model: { model: string; provider: string; requests: number; tokens: number; cost_idr: number }[];
}

interface UsageRow {
  request_id: string;
  created_at: string;
  model: string | null;
  provider: string | null;
  display_name: string | null;
  family: string | null;
  status: string;
  kind: string;
  input_tokens: number;
  cached_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_idr: number;
  latency_ms: number | null;
}

interface DashData {
  balance: Balance;
  current: Summary;
  previous: Summary;
  keyCount: number;
  projects: ProjectOption[];
  models: CatalogModel[];
  recent: UsageRow[];
}

type Metric = 'cost' | 'requests' | 'tokens';

const METRICS: ReadonlyArray<{ id: Metric; label: string }> = [
  { id: 'cost', label: 'Biaya' },
  { id: 'requests', label: 'Request' },
  { id: 'tokens', label: 'Token' },
];

const STATUS_LABEL: Record<string, string> = {
  COMPLETED: 'Selesai',
  FAILED: 'Gagal',
  STREAMING: 'Berjalan',
  PENDING: 'Menunggu',
  CANCELLED: 'Dibatalkan',
};

const DAY = 86_400_000;

function iso(time: number) {
  return encodeURIComponent(new Date(time).toISOString());
}

function dayKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 30 days, zero-filled. */
function fillDays(rows: DayRow[]): DayRow[] {
  const byDate = new Map(rows.map((row) => [row.date, row]));
  const today = new Date();
  return Array.from({ length: 30 }, (_, i) => {
    const key = dayKey(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29 + i));
    return byDate.get(key) ?? { date: key, requests: 0, tokens: 0, cost_idr: 0 };
  });
}

function tokenTotal(s: Summary) {
  const t = s.totals;
  return t.input_tokens + t.cached_tokens + t.output_tokens + t.reasoning_tokens;
}

function runway(available: number, spent30: number): string {
  if (available <= 0) return 'Saldo kosong';
  const perDay = spent30 / 30;
  if (perDay <= 0) return 'Belum ada pemakaian';
  const days = Math.floor(available / perDay);
  if (days > 365) return 'Cukup untuk lebih dari setahun';
  return `Cukup untuk ±${num(days)} hari`;
}

export function Dashboard() {
  const [data, setData] = useState<DashData | null>(null);
  const [metric, setMetric] = useState<Metric>('cost');
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<CreatedKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const now = Date.now();
    try {
      const [balance, current, previous, keys, projects, models, recent] = await Promise.all([
        api<Balance>('/v1/wallet/balance'),
        api<Summary>(`/v1/usage/summary?from=${iso(now - 30 * DAY)}&to=${iso(now)}`),
        api<Summary>(`/v1/usage/summary?from=${iso(now - 60 * DAY)}&to=${iso(now - 30 * DAY)}`),
        api<{ data: Array<{ status: string }> }>('/v1/api-keys'),
        api<{ data: ProjectOption[] }>('/v1/projects'),
        api<{ data: CatalogModel[] }>('/v1/models').catch(() => ({ data: [] as CatalogModel[] })),
        api<{ data: UsageRow[] }>('/v1/usage?page_size=6').catch(() => ({ data: [] as UsageRow[] })),
      ]);
      setData({
        balance,
        current,
        previous,
        keyCount: keys.data.filter((key) => key.status === 'ACTIVE').length,
        projects: projects.data,
        models: models.data,
        recent: recent.data,
      });
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const days = useMemo(() => fillDays(data?.current.by_day ?? []), [data]);
  const catalog = useMemo(() => new Map((data?.models ?? []).map((m) => [m.id, m])), [data]);
  const popular = useMemo(() => popularModels(data?.models ?? [], 4), [data]);

  if (loading) return <Loading label="Memuat beranda" />;
  if (error || !data) return <Alert kind="error">{error ?? 'Data beranda tidak tersedia.'}</Alert>;

  const { balance, current, previous, keyCount, projects, recent } = data;
  const cur = current.totals;
  const prev = previous.totals;
  const today = days[days.length - 1];
  const successRate = cur.requests > 0 ? (cur.successful_requests / cur.requests) * 100 : 100;
  const lowBalance = balance.available_balance < 10_000;
  const anyRequest = cur.requests + prev.requests > 0 || recent.length > 0;

  const steps: Array<{ title: string; done: boolean; detail: string; action: (primary: boolean) => ReactNode }> = [
    {
      title: 'Isi saldo',
      done: balance.available_balance > 0,
      detail: balance.available_balance > 0 ? `Saldo ${idr(balance.available_balance)}` : 'Setiap request dibayar dari saldo Rupiah.',
      action: (primary) => (
        <Link className={primary ? 'btn btn-sm btn-primary' : 'btn btn-sm'} to="/wallet">
          Isi saldo
        </Link>
      ),
    },
    {
      title: 'Buat API key',
      done: keyCount > 0,
      detail: keyCount > 0 ? `${keyCount} key aktif` : 'Dipakai aplikasi Anda untuk memanggil API.',
      action: (primary) => (
        <button className={primary ? 'btn btn-sm btn-primary' : 'btn btn-sm'} onClick={() => setCreating(true)} disabled={projects.length === 0}>
          Buat key
        </button>
      ),
    },
    {
      title: 'Kirim request pertama',
      done: anyRequest,
      detail: anyRequest ? `${num(cur.requests)} request bulan ini` : 'Coba model langsung dari Playground.',
      action: (primary) => (
        <Link className={primary ? 'btn btn-sm btn-primary' : 'btn btn-sm'} to="/playground">
          Buka playground
        </Link>
      ),
    },
  ];
  const doneCount = steps.filter((step) => step.done).length;
  const nextIndex = steps.findIndex((step) => !step.done);

  const metricValue = (row: DayRow) => (metric === 'cost' ? row.cost_idr : metric === 'requests' ? row.requests : row.tokens);
  const metricFormat = (v: number) => (metric === 'cost' ? idr(v) : compact(v));
  const metricLabel = METRICS.find((m) => m.id === metric)?.label ?? '';
  const idle = cur.requests === 0;
  const spark = (values: number[]) => (values.some((v) => v > 0) ? values : undefined);
  const delta = (now: number, before: number, invert = false) =>
    now > 0 || before > 0 ? <Delta current={now} previous={before} invert={invert} /> : undefined;

  const topModels = [...current.by_model].sort((a, b) => b.cost_idr - a.cost_idr).slice(0, 5);
  const topMax = Math.max(...topModels.map((row) => row.cost_idr), 0) || 1;
  const spendTotal = cur.cost_idr || 1;

  return (
    <>
      <PageHeader
        title="Beranda"
        subtitle="Ringkasan 30 hari terakhir di semua proyek."
        actions={
          <Link className="btn btn-primary" to="/playground">
            <Icon name="terminal" size={16} />
            Buka playground
          </Link>
        }
      />

      {doneCount < steps.length && (
        <section className="card onb mb" aria-label="Langkah awal">
          <div className="onb-head">
            <div>
              <h2>Mulai dalam tiga langkah</h2>
              <p className="subtitle">Setelah ini aplikasi Anda bisa memanggil semua model lewat satu API.</p>
            </div>
            <div className="onb-progress">
              <span>
                {doneCount} dari {steps.length} selesai
              </span>
              <div className="meter">
                <span style={{ width: `${(doneCount / steps.length) * 100}%` }} />
              </div>
            </div>
          </div>
          <ol className="onb-steps">
            {steps.map((step, index) => (
              <li key={step.title} className={['onb-step', step.done ? 'done' : '', index === nextIndex ? 'next' : ''].filter(Boolean).join(' ')}>
                <span className="onb-check">{step.done ? <Icon name="check" size={14} /> : index + 1}</span>
                <div className="grow">
                  <div className="onb-title">{step.title}</div>
                  <div className="onb-detail">{step.detail}</div>
                </div>
                {!step.done && step.action(index === nextIndex)}
              </li>
            ))}
          </ol>
        </section>
      )}

      {lowBalance && steps[0].done && (
        <Alert kind="warn">
          Saldo tinggal {idr(balance.available_balance)}. Jika habis, request akan ditolak. <Link to="/wallet">Isi saldo</Link>
        </Alert>
      )}

      <div className="grid grid-4 mb dash-stats">
        <StatTile
          label="Saldo tersedia"
          value={idr(balance.available_balance)}
          tone={lowBalance ? 'critical' : undefined}
          sub={balance.reserved_balance > 0 ? `${idr(balance.reserved_balance)} sedang ditahan` : runway(balance.available_balance, cur.cost_idr)}
        />
        <StatTile
          label="Pengeluaran"
          value={idr(cur.cost_idr)}
          delta={delta(cur.cost_idr, prev.cost_idr, true)}
          sub={idle ? 'Belum ada pemakaian' : `Hari ini ${idr(today?.cost_idr ?? 0)}`}
          spark={spark(days.map((d) => d.cost_idr))}
        />
        <StatTile
          label="Request"
          value={num(cur.requests)}
          delta={delta(cur.requests, prev.requests)}
          sub={idle ? 'Belum ada request' : `${percent(successRate)} berhasil · rata-rata ${ms(cur.avg_latency_ms)}`}
          tone={successRate < 95 ? 'warn' : undefined}
          spark={spark(days.map((d) => d.requests))}
        />
        <StatTile
          label="Token"
          value={compact(tokenTotal(current))}
          delta={delta(tokenTotal(current), tokenTotal(previous))}
          sub={idle ? 'Belum ada token terpakai' : `${compact(cur.input_tokens + cur.cached_tokens)} masuk · ${compact(cur.output_tokens + cur.reasoning_tokens)} keluar`}
          spark={spark(days.map((d) => d.tokens))}
        />
      </div>

      <div className="dash-row mb">
        <Card
          title="Aktivitas"
          description={`${metricLabel} per hari, 30 hari terakhir`}
          action={<Segmented label="Metrik" options={METRICS} value={metric} onChange={setMetric} />}
        >
          <TimeSeriesChart
            data={days.some((d) => metricValue(d) > 0) ? days : []}
            emptyMessage="Belum ada aktivitas dalam 30 hari terakhir"
            xLabel={(row) => dateShort(row.date)}
            series={[{ key: metric, label: metricLabel, value: metricValue }]}
            formatValue={metricFormat}
            height={250}
          />
        </Card>

        <Card
          title="Model teratas"
          description="Menurut biaya, 30 hari terakhir"
          action={
            <Link className="btn btn-sm btn-ghost" to="/usage">
              Detail
              <Icon name="arrow" size={14} />
            </Link>
          }
        >
          {topModels.length === 0 ? (
            <Empty icon="bars">Belum ada pemakaian.</Empty>
          ) : (
            <ul className="top-models">
              {topModels.map((row, index) => {
                const entry = catalog.get(row.model);
                return (
                  <li key={row.model}>
                    <ModelLogo model={row.model} family={entry?.family} provider={row.provider} size={30} />
                    <div className="grow">
                      <div className="top-line">
                        <span className="top-name">
                          {entry?.display_name ?? row.model}
                          <span className="top-sub"> · {num(row.requests)} request</span>
                        </span>
                        <span className="top-cost" title={`${percent((row.cost_idr / spendTotal) * 100)} dari total`}>
                          {idr(row.cost_idr)}
                        </span>
                      </div>
                      <div className="meter">
                        <span style={{ width: `${Math.max((row.cost_idr / topMax) * 100, 2)}%`, background: seriesColor(index) }} />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      {popular.length > 0 && (
        <section className="mb">
          <div className="section-title">
            Coba model
            <Link to="/models" className="dash-more">
              Semua model
              <Icon name="arrow" size={14} />
            </Link>
          </div>
          <div className="try-grid">
            {popular.map((m) => (
              <Link key={m.id} to={playgroundHref(m)} className="try-card">
                <div className="try-head">
                  <ModelLogo model={m.id} family={m.family} provider={providerOf(m)} size={36} />
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="try-name">{m.display_name}</div>
                    <div className="try-provider">
                      {providerName(providerOf(m))} · {MODALITY_LABEL[modalityOf(m)]}
                    </div>
                  </div>
                </div>
                <TryPrice model={m} />
                <span className="try-go">
                  Coba di playground
                  <Icon name="arrow" size={14} />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Card
        title="Request terakhir"
        action={
          <Link className="btn btn-sm btn-ghost" to="/usage">
            Lihat semua
            <Icon name="arrow" size={14} />
          </Link>
        }
      >
        {recent.length === 0 ? (
          <Empty icon="list" action={<Link className="btn btn-sm" to="/playground">Buka playground</Link>}>
            Belum ada request.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="dash-recent">
              <thead>
                <tr>
                  <th>Waktu</th>
                  <th>Model</th>
                  <th className="num">Token</th>
                  <th className="num">Biaya</th>
                  <th className="num">Latensi</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((row) => (
                  <tr key={row.request_id}>
                    <td className="nowrap dr-time" title={dateTime(row.created_at)}>
                      {relative(row.created_at)}
                    </td>
                    <td className="dr-model">
                      <ModelChip
                        model={row.model ?? '—'}
                        name={row.display_name ?? catalog.get(row.model ?? '')?.display_name}
                        provider={row.provider}
                        family={row.family}
                        size={24}
                        showId={false}
                      />
                    </td>
                    <td className="num nowrap dr-tok">
                      {row.total_tokens > 0 ? (
                        <>
                          {compact(row.input_tokens + row.cached_tokens)}
                          <span className="muted"> → </span>
                          {compact(row.output_tokens)}
                          <span className="dr-unit"> token</span>
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num strong nowrap dr-cost">{idr(row.cost_idr)}</td>
                    <td className="num nowrap dr-lat">{ms(row.latency_ms)}</td>
                    <td className="dr-status">
                      <Badge status={row.status}>{STATUS_LABEL[row.status] ?? row.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <CreateKeyModal
          projects={projects}
          onClose={() => setCreating(false)}
          onCreated={async (result) => {
            setCreating(false);
            setRevealed(result);
            await load();
          }}
        />
      )}
      {revealed && <KeyRevealModal created={revealed} onDone={() => setRevealed(null)} />}
    </>
  );
}

function TryPrice({ model }: { model: CatalogModel }) {
  const p = model.pricing;
  const mode = modalityOf(model);
  if (!p) return <div className="try-price muted">Harga belum tersedia</div>;
  if (mode === 'text') {
    return (
      <div className="try-price">
        <div>
          <span>Input</span>
          <b>{idr(p.input_per_1m ?? 0)}</b>
        </div>
        <div>
          <span>Output</span>
          <b>{idr(p.output_per_1m ?? 0)}</b>
        </div>
        <small>per 1 juta token</small>
      </div>
    );
  }
  return (
    <div className="try-price">
      <div>
        <span>{mode === 'image' ? 'Per gambar' : 'Per detik'}</span>
        <b>{idr(mode === 'image' ? p.per_image : p.per_second_720p)}</b>
      </div>
      <small>{mode === 'image' ? 'Dibayar per gambar jadi' : 'Perkiraan untuk 720p'}</small>
    </div>
  );
}
